import React, { useState } from 'react';
import { DollarSign, Eye, EyeOff, ArrowRight, Check, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { PossibleLogo } from '../components/ui/PossibleLogo';
export function SignupPage() {
  const navigate = useNavigate();
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirm: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name) e.name = 'Name is required';
    if (!form.email) e.email = 'Email is required';
    if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match';
    if (!agreed) e.terms = 'You must agree to the terms';
    return e;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    await new Promise(r => setTimeout(r, 1400));
    setLoading(false);
    navigate('/dashboard');
  };
  const update = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({
      ...f,
      [key]: e.target.value
    }));
    setErrors(err => ({
      ...err,
      [key]: ''
    }));
  };
  const perks = ['Free forever plan available', 'No credit card required', 'Bank-level security', 'Cancel anytime'];
  return <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex" data-id="element-1053">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-900 flex-col justify-between p-12 relative overflow-hidden" data-id="element-1054">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/60 to-gray-900" data-id="element-1055" />
        <div className="relative z-10" data-id="element-1056">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-id="element-1057">
            <PossibleLogo height={18} variant="white" data-id="element-1058" />
          </button>
        </div>
        <div className="relative z-10" data-id="element-1059">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-1060">
            Start your journey to financial freedom.
          </h2>
          <p className="text-gray-400 text-lg mb-8" data-id="element-1061">
            Set up in 2 minutes. See results in 2 weeks.
          </p>
          <div className="space-y-3" data-id="element-1062">
            {perks.map((perk, i) => <div key={i} className="flex items-center gap-3" data-id="element-1063">
                <div className="w-5 h-5 rounded-full bg-accent-500/20 flex items-center justify-center" data-id="element-1064">
                  <Check size={11} className="text-accent-400" data-id="element-1065" />
                </div>
                <span className="text-gray-300 text-sm" data-id="element-1066">{perk}</span>
              </div>)}
          </div>
        </div>
        <div className="relative z-10 bg-white/5 border border-white/10 rounded-2xl p-4" data-id="element-1067">
          <p className="text-gray-300 text-sm italic mb-3" data-id="element-1068">
            "I paid off $12,000 in debt in 18 months using Possible's budget
            coaching."
          </p>
          <div className="flex items-center gap-2" data-id="element-1069">
            <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center text-black text-xs font-bold" data-id="element-1070">
              J
            </div>
            <div data-id="element-1071">
              <p className="text-white text-xs font-semibold" data-id="element-1072">Jordan Lee</p>
              <p className="text-gray-500 text-xs" data-id="element-1073">Possible user since 2024</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 overflow-y-auto" data-id="element-1074">
        <div className="w-full max-w-md" data-id="element-1075">
          <div className="flex items-center justify-between mb-8 lg:justify-end" data-id="element-1076">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 lg:hidden" data-id="element-1077">
              <div className="w-4 h-6 rounded-sm bg-gray-900 dark:bg-white shrink-0" data-id="element-1078" />
              <span className="font-bold text-gray-900 dark:text-white" data-id="element-1079">
                Possible
              </span>
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500" data-id="element-1080">
              {isDark ? <Sun size={17} data-id="element-1081" /> : <Moon size={17} data-id="element-1082" />}
            </button>
          </div>

          <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-1" data-id="element-1083">
            Create your account
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-7" data-id="element-1084">
            Get started for free — no credit card needed
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1085">
            {[{
            key: 'name',
            label: 'Full Name',
            type: 'text',
            placeholder: 'Alex Johnson'
          }, {
            key: 'email',
            label: 'Email',
            type: 'email',
            placeholder: 'alex@example.com'
          }].map(field => <div key={field.key} data-id="element-1086">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block" data-id="element-1087">
                  {field.label}
                </label>
                <input type={field.type} value={form[field.key as keyof typeof form]} onChange={update(field.key)} placeholder={field.placeholder} className={`w-full bg-white dark:bg-dark-card border rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none transition-colors ${errors[field.key] ? 'border-red-400' : 'border-gray-200 dark:border-dark-border focus:border-primary-400 dark:focus:border-primary-500'}`} data-id="element-1088" />
                {errors[field.key] && <p className="text-xs text-red-500 mt-1" data-id="element-1089">
                    {errors[field.key]}
                  </p>}
              </div>)}

            <div data-id="element-1090">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block" data-id="element-1091">
                Password
              </label>
              <div className="relative" data-id="element-1092">
                <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={update('password')} placeholder="Min. 8 characters" className={`w-full bg-white dark:bg-dark-card border rounded-xl px-4 py-3 pr-11 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none transition-colors ${errors.password ? 'border-red-400' : 'border-gray-200 dark:border-dark-border focus:border-primary-400 dark:focus:border-primary-500'}`} data-id="element-1093" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1094">
                  {showPassword ? <EyeOff size={16} data-id="element-1095" /> : <Eye size={16} data-id="element-1096" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1" data-id="element-1097">{errors.password}</p>}
            </div>

            <div data-id="element-1098">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block" data-id="element-1099">
                Confirm Password
              </label>
              <input type="password" value={form.confirm} onChange={update('confirm')} placeholder="••••••••" className={`w-full bg-white dark:bg-dark-card border rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none transition-colors ${errors.confirm ? 'border-red-400' : 'border-gray-200 dark:border-dark-border focus:border-primary-400 dark:focus:border-primary-500'}`} data-id="element-1100" />
              {errors.confirm && <p className="text-xs text-red-500 mt-1" data-id="element-1101">{errors.confirm}</p>}
            </div>

            <div data-id="element-1102">
              <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-1103">
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-primary-500" data-id="element-1104" />
                <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1105">
                  I agree to the{' '}
                  <button type="button" className="text-primary-500 hover:text-primary-600 font-medium" data-id="element-1106">
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button type="button" className="text-primary-500 hover:text-primary-600 font-medium" data-id="element-1107">
                    Privacy Policy
                  </button>
                </span>
              </label>
              {errors.terms && <p className="text-xs text-red-500 mt-1" data-id="element-1108">{errors.terms}</p>}
            </div>

            <button type="submit" disabled={loading} className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-1109">
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1110" /> : <>
                  Create account <ArrowRight size={16} data-id="element-1111" />
                </>}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6" data-id="element-1112">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-primary-500 hover:text-primary-600 font-semibold" data-id="element-1113">
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>;
}